import { CheckIcon, PencilLine } from "lucide-react";
import TargetSection from "./components/TargetSection";
import CareerProgressionSection from "./components/CareerProgressionSection";
import KeyAreaSection from "./components/KeyAreaSection";
import SkillSection from "./components/SkillSection";
import ProfileContainer from "./components/ProfileContainer";
import { useEffect, useState } from "react";
import { getAspirationDetail } from "@endpoints/candidate.service";
import { useDispatch } from "react-redux";
import { setAspirationDetails } from "@store/actions/auth.action";
import LoadingAnimation from "@assets/svg/LoadingAnimation";

const matchInsights = [
  {
    label: "Relevant experience",
    desc: "Your background aligns well with this role level.",
  },
  {
    label: "In-demand skils",
    desc: "You have skills highly valued in target roles.",
  },
  {
    label: "Clear progression",
    desc: "Your career path shows consistent growth.",
  },
  {
    label: "Location alignment",
    desc: "Your preferred location has strong opportunities.",
  },
];

const AspirationPage3 = () => {
  const dispatch = useDispatch();

  const [isLoadingDetails, setIsLoadingDetails] = useState(true);

  useEffect(() => {
    const fetchAspirationDetails = async () => {
      try {
        setIsLoadingDetails(true);
        const { success, data } = await getAspirationDetail();
        if (success) {
          dispatch(setAspirationDetails(data));
        }
      } catch (error) {
        console.error("Error fetching aspiration details", error);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchAspirationDetails();
  }, []);

  return (
    <div className="flex flex-col flex-1 rounded-md gap-5 min-h-0 overflow-y-scroll no-scrollbar">
      {isLoadingDetails ? (
        <LoadingAnimation color="#334154" className="m-auto h-7 w-7" />
      ) : (
        <>
          <ProfileContainer />
          {/* Main body  */}
          <div className="flex w-full gap-5">
            {/* Cols 1 */}
            <div className="flex flex-col gap-5 flex-1">
              <TargetSection />
              <SkillSection />
              <CareerProgressionSection />
              <KeyAreaSection />
            </div>
            {/* Cols 2 */}
            <div className="flex flex-col gap-5 w-[28%]">
              {/* Container 1 */}
              <div className="flex flex-col bg-white border rounded-xl p-5 gap-4">
                <div className="flex items-center justify-between">
                  <span className="uppercase font-bold text-xs text-slate-800">
                    Profile status
                  </span>
                  <div className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded-full">
                    Active
                  </div>
                </div>
                <div className="w-full h-px bg-gray-200" />
                <span className="font-semibold text-light-black text-xss leading-4">
                  This profile is actively matched with relevant job
                  opportunities.
                </span>
                {/* Match strength */}
                <div className="flex flex-col gap-1.5 my-2">
                  <span className="font-bold uppercase text-xs text-slate-800">
                    match strength
                  </span>
                  <span className="font-bold text-4xl text-slate-800">92%</span>
                  <div className="rounded-full overflow-hidden h-2 w-full bg-gray-200">
                    <div className="bg-slate-800 h-full w-[92%]" />
                  </div>
                </div>
                {/* Excellent match */}
                <div className="flex flex-col gap-2">
                  <span className="font-bold text-sm text-slate-800">
                    Excellent Match
                  </span>
                  <span className="text-xss font-semibold leading-4 text-light-black">
                    Your experience and skills strongly align with your career
                    aspirations.
                  </span>
                  <span className="font-bold text-sm text-slate-800">
                    What does this mean?
                  </span>
                </div>
              </div>
              {/* Container 2 */}
              <div className="flex flex-col bg-white border rounded-xl p-5 gap-2">
                <span className="uppercase font-bold text-xs text-slate-800">
                  Match insights
                </span>

                <div className="flex flex-col">
                  {matchInsights?.map((insight, insightIndex) => {
                    return (
                      <div
                        key={insight?.label}
                        className={`flex gap-4 py-3 ${insightIndex !== 0 ? "border-t" : ""}`}
                      >
                        <CheckIcon size={17} />
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-bold text-slate-800">
                            {insight?.label}
                          </span>
                          <span className="text-xss font-medium text-light-black">
                            {insight?.desc}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <span className="font-bold text-xs text-slate-800">
                  View full match insights
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AspirationPage3;
